import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { DataTable, Button, Input, Checkbox, ToggleSwitch } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { eventGuestService, eventFieldDefService } from '../services'
import { useEventDialog } from '../hooks'

const RESPONSE_PILLS = { yes: { label: 'Ja', className: 'pill-green' }, no: { label: 'Nej', className: 'pill-red' } }
const BOOL_PILLS = { true: { label: 'Ja', className: 'pill-green' }, false: { label: 'Nej', className: 'pill-grey' } }

const EMPTY_FILTERS = { response: [], invite: [], custom: {} }

// "Filter (N) ▾" popover — faceted filtering the shared <DataTable> toolbar
// can't do (it only has free-text search). Same outside-click/close plumbing
// as BulkActionsMenu below and recruitment's .cd-actions-menu. Counts are
// computed off the full (unfiltered) guest set so they don't shift as you
// narrow, matching how faceted filters read elsewhere.
const FilterPopover = ({ fieldDefs, guests, filters, setFilters, activeCount }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef()

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const toggleIn = (facet, value) => setFilters(prev => {
    const arr = prev[facet]
    return { ...prev, [facet]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value] }
  })

  const toggleCustomBool = (key) => setFilters(prev => {
    const custom = { ...prev.custom }
    if (custom[key] === true) delete custom[key]; else custom[key] = true
    return { ...prev, custom }
  })

  const toggleCustomValue = (key, value) => setFilters(prev => {
    const custom = { ...prev.custom }
    const arr = Array.isArray(custom[key]) ? custom[key] : []
    const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value]
    if (next.length) custom[key] = next; else delete custom[key]
    return { ...prev, custom }
  })

  const respCount = (v) => guests.filter(g => (g.event_guest_response || 'na') === v).length
  const invCount = (v) => guests.filter(g => (g.invite_sent ? 'sent' : 'unsent') === v).length

  // Distinct non-empty values a text/number custom field actually holds, with counts.
  const customValues = (fd) => {
    const counts = {}
    guests.forEach(g => {
      const raw = g.event_guest_custom_data?.[fd.field_key]
      if (raw === null || raw === undefined || raw === '') return
      const key = String(raw)
      counts[key] = (counts[key] || 0) + 1
    })
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0], 'sv')).map(([value, count]) => ({ value, count }))
  }

  return (
    <div className="ev-filter-wrap" ref={wrapRef}>
      <Button
        value={`Filter${activeCount ? ` (${activeCount})` : ''} ▾`}
        className={'button' + (activeCount ? ' ev-filter-active' : '')}
        onClick={() => setOpen(o => !o)}
      />
      {open &&
        <div className="ev-filter-popover">
          <div className="ev-facet">
            <h4>Svar</h4>
            <Checkbox name="f-resp-yes" label={`Ja (${respCount('yes')})`} checked={filters.response.includes('yes')} onChange={() => toggleIn('response', 'yes')} />
            <Checkbox name="f-resp-no" label={`Nej (${respCount('no')})`} checked={filters.response.includes('no')} onChange={() => toggleIn('response', 'no')} />
            <Checkbox name="f-resp-na" label={`Inget svar (${respCount('na')})`} checked={filters.response.includes('na')} onChange={() => toggleIn('response', 'na')} />
          </div>

          <div className="ev-facet">
            <h4>Inbjudan</h4>
            <Checkbox name="f-inv-sent" label={`Skickad (${invCount('sent')})`} checked={filters.invite.includes('sent')} onChange={() => toggleIn('invite', 'sent')} />
            <Checkbox name="f-inv-unsent" label={`Ej skickad (${invCount('unsent')})`} checked={filters.invite.includes('unsent')} onChange={() => toggleIn('invite', 'unsent')} />
          </div>

          {fieldDefs.map(fd => (
            <div className="ev-facet" key={fd.event_field_def_id}>
              <h4>{fd.field_label}</h4>
              {fd.field_type === 'boolean'
                ? <ToggleSwitch label={`Endast ${fd.field_label}`} value={filters.custom[fd.field_key] === true} onChange={() => toggleCustomBool(fd.field_key)} />
                : customValues(fd).length === 0
                  ? <div className="ev-optional">Inga värden än</div>
                  : customValues(fd).map(({ value, count }) => (
                      <Checkbox
                        key={value}
                        name={`f-${fd.field_key}-${value}`}
                        label={`${value} (${count})`}
                        checked={Array.isArray(filters.custom[fd.field_key]) && filters.custom[fd.field_key].includes(value)}
                        onChange={() => toggleCustomValue(fd.field_key, value)}
                      />
                    ))
              }
            </div>
          ))}

          {activeCount > 0 &&
            <button type="button" className="ev-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)}>Rensa alla filter</button>
          }
        </div>
      }
    </div>
  )
}

// Dark contextual action bar shown in place of the toolbar once rows are
// selected — collects every bulk action in one place. "Sätt svar" is a Ja/Nej
// segment (the most common action, one click, no dialog); the rest open their
// existing dialogs. Delete is held apart on the right so it doesn't sit next
// to the safe actions.
const SelectionBar = ({ count, onClear, onSetResponse, onSend, onBulkEdit, onExport, onDelete }) => (
  <div className="ev-selbar">
    <button type="button" className="ev-selbar-x" onClick={onClear} title="Rensa markering">✕</button>
    <span className="ev-selbar-count">{count} valda</span>
    <span className="ev-selbar-label">Sätt svar</span>
    <div className="ev-selbar-seg">
      <button type="button" className="y" onClick={() => onSetResponse('yes')}>Ja</button>
      <button type="button" className="n" onClick={() => onSetResponse('no')}>Nej</button>
    </div>
    <button type="button" className="ev-selbar-btn" onClick={onSend}>Skicka inbjudan</button>
    <button type="button" className="ev-selbar-btn" onClick={onBulkEdit}>Redigera fält…</button>
    <button type="button" className="ev-selbar-btn" onClick={onExport}>Exportera</button>
    <span className="ev-selbar-spacer" />
    <button type="button" className="ev-selbar-btn danger" onClick={onDelete}>Ta bort</button>
  </div>
)

const GuestList = ({ event }) => {
  const eventSlug = event.event_slug

  const [guests, setGuests] = useState([])
  const [fieldDefs, setFieldDefs] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState(EMPTY_FILTERS)

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showAddGuestsDialog, showEditGuestDialog, showSendInvitesDialog, showDeleteGuestDialog, showBulkEditGuestDialog } = useEventDialog()

  const fetchItems = useCallback(() => {
    Promise.all([
      eventGuestService.getByEvent(eventSlug),
      eventFieldDefService.getByEvent(eventSlug),
    ])
      .then(([guestsRes, fieldDefsRes]) => {
        setGuests(guestsRes.guests || [])
        setFieldDefs(fieldDefsRes.fieldDefs || [])
      })
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoading(false))
  }, [eventSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchItems() }, [fetchItems])

  // target is 'all' (every guest with an email, resends included) or
  // 'selected' (the checked rows) — see openSendInvitesDialog below.
  const sendInvitesTo = (target) => {
    const targetGuests = target === 'all' ? guests.filter(g => g.event_guest_email) : selectedItems
    const uuids = targetGuests.map(g => g.event_guest_uuid)
    return eventGuestService.sendInvites(uuids)
      .then(res => { setSelectedItems([]); fetchItems(); showMessage(res.message || 'Inbjudan skickad') })
  }

  const openSendInvitesDialog = () => {
    const allCount = guests.filter(g => g.event_guest_email).length
    showSendInvitesDialog(allCount, selectedItems.length, sendInvitesTo)
  }

  const markResponse = (items, response) => {
    Promise.all(items.map(g => eventGuestService.update(g.event_guest_uuid, { event_guest_response: response })))
      .then(() => { fetchItems(); showMessage(items.length > 1 ? `Svar uppdaterat för ${items.length} gäster` : 'Svar uppdaterat') })
      .catch(err => showMessage(JSON.stringify(err), 'error'))
  }

  const addGuests = () => {
    // Persons already on this event's guest list are excluded from the picker
    // so the same person can't be added twice (guests created without a
    // network link have a null person_uuid and simply aren't in this set).
    const existingPersonUuids = guests.map(g => g.event_guest_person_uuid).filter(Boolean)
    showAddGuestsDialog(eventSlug, fieldDefs, existingPersonUuids, () => { hideModalDialog(); fetchItems(); showMessage('Gäster tillagda') })
  }

  // Full single-guest edit (name/company/email/response/custom fields) —
  // distinct from bulkEdit below, which edits the checked rows across a grid.
  // Always targets the first selected/clicked row, same "items[0]" convention
  // as EventList.js's editEvent.
  const editGuest = (items) => {
    showEditGuestDialog(items[0], fieldDefs, () => { hideModalDialog(); fetchItems(); showMessage('Gäst uppdaterad') })
  }

  // Grid bulk edit — the form hands back only the guests whose values changed
  // as [{ uuid, payload }]; per-guest values can differ (e.g. individual golf
  // handicaps), so each is a separate `update` (custom_data merges server-side)
  // rather than a single one-value `/bulk` call. Guests are distinct rows, so
  // the updates are independent and run in parallel.
  const bulkEdit = (items) => {
    showBulkEditGuestDialog(items, fieldDefs, async (changes) => {
      await Promise.all(changes.map(c => eventGuestService.update(c.uuid, c.payload)))
        .then(() => { hideModalDialog(); setSelectedItems([]); fetchItems(); showMessage(`${changes.length} gäst${changes.length > 1 ? 'er' : ''} uppdaterade`) })
        .catch(err => { showMessage(err.message || 'Kunde inte uppdatera gäster', 'error'); throw err })
    })
  }

  // Client-side only, same UTF-8-BOM pattern as recruitment/network's
  // exportSelected — no backend export endpoint exists in this repo.
  const exportSelected = (items) => {
    const headers = ['Namn', 'Företag', 'E-post', 'Inbjudan skickad', 'Svar', ...fieldDefs.map(fd => fd.field_label)]
    const escape = (value) => {
      const s = value == null ? '' : String(value)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = items.map(g => [
      g.event_guest_name,
      g.event_guest_company_name,
      g.event_guest_email,
      g.invite_sent ? 'Ja' : 'Nej',
      g.event_guest_response === 'yes' ? 'Ja' : g.event_guest_response === 'no' ? 'Nej' : '',
      ...fieldDefs.map(fd => g.event_guest_custom_data?.[fd.field_key]),
    ].map(escape).join(','))

    const csv = '﻿' + [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gastlista_${event.event_title}_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const deleteGuests = (items) => {
    showDeleteGuestDialog(items, async (data) => {
      await Promise.all(data.map(g => eventGuestService.remove(g.event_guest_uuid)))
        .then(() => { hideModalDialog(); setSelectedItems([]); fetchItems(); showMessage('Borttaget') })
        .catch(err => showMessage(JSON.stringify(err), 'error'))
    })
  }

  // DataTable resolves every column via a plain item[column.id] lookup (no
  // accessor-function support) — so per-event custom fields must be flattened
  // onto each row before being handed to <DataTable>.
  const guestsFlat = useMemo(() => guests.map(g => ({ ...g, ...g.event_guest_custom_data })), [guests])

  // Faceted filtering + free-text search happen here (the toolbar is bespoke,
  // DataTable's built-in one is hidden), so DataTable only ever sees rows that
  // already pass every active filter.
  const filteredGuests = useMemo(() => {
    const term = search.trim().toLowerCase()
    const matchesSearch = (g) => !term ||
      [g.event_guest_name, g.event_guest_company_name, g.event_guest_email]
        .some(v => (v || '').toLowerCase().includes(term))

    const matchesFilters = (g) => {
      if (filters.response.length && !filters.response.includes(g.event_guest_response || 'na')) return false
      if (filters.invite.length && !filters.invite.includes(g.invite_sent ? 'sent' : 'unsent')) return false
      for (const [key, sel] of Object.entries(filters.custom)) {
        const val = g.event_guest_custom_data?.[key]
        if (sel === true) { if (val !== true) return false }
        else if (Array.isArray(sel) && sel.length && !sel.includes(String(val ?? ''))) return false
      }
      return true
    }

    return guestsFlat.filter(g => matchesSearch(g) && matchesFilters(g))
  }, [guestsFlat, search, filters])

  // Keep the selection confined to currently-visible rows — since filtering
  // happens here (not inside DataTable), a row that a filter hides must also
  // leave the selection, so the selection bar's count and every bulk action
  // only ever act on rows the user can see.
  useEffect(() => {
    const visible = new Set(filteredGuests.map(g => g.event_guest_uuid))
    setSelectedItems(prev => {
      const pruned = prev.filter(g => visible.has(g.event_guest_uuid))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [filteredGuests])

  // One removable chip per active filter selection, labelled the way the user
  // set it (facet value, not internal key).
  const chips = useMemo(() => {
    const out = []
    const respLabel = { yes: 'Svar: Ja', no: 'Svar: Nej', na: 'Svar: Inget svar' }
    const invLabel = { sent: 'Inbjudan: Skickad', unsent: 'Inbjudan: Ej skickad' }
    filters.response.forEach(v => out.push({ label: respLabel[v], remove: () => setFilters(p => ({ ...p, response: p.response.filter(x => x !== v) })) }))
    filters.invite.forEach(v => out.push({ label: invLabel[v], remove: () => setFilters(p => ({ ...p, invite: p.invite.filter(x => x !== v) })) }))
    Object.entries(filters.custom).forEach(([key, sel]) => {
      const fd = fieldDefs.find(f => f.field_key === key)
      const name = fd?.field_label || key
      if (sel === true) out.push({ label: name, remove: () => setFilters(p => { const c = { ...p.custom }; delete c[key]; return { ...p, custom: c } }) })
      else if (Array.isArray(sel)) sel.forEach(v => out.push({
        label: `${name}: ${v}`,
        remove: () => setFilters(p => { const c = { ...p.custom }; const next = (c[key] || []).filter(x => x !== v); if (next.length) c[key] = next; else delete c[key]; return { ...p, custom: c } }),
      }))
    })
    return out
  }, [filters, fieldDefs])

  const activeFilterCount = chips.length

  const dynamicColumns = fieldDefs.map(fd => ({
    id: fd.field_key,
    name: fd.field_label,
    type: fd.field_type === 'boolean' ? 'boolean' : undefined,
    values: fd.field_type === 'boolean' ? BOOL_PILLS : undefined,
  }))

  const hasSelection = selectedItems.length > 0

  return (
    <div className="ev-guestlist">
      <div className="ev-guestlist-controls">
        {hasSelection
          ? <SelectionBar
              count={selectedItems.length}
              onClear={() => setSelectedItems([])}
              onSetResponse={(r) => markResponse(selectedItems, r)}
              onSend={openSendInvitesDialog}
              onBulkEdit={() => bulkEdit(selectedItems)}
              onExport={() => exportSelected(selectedItems)}
              onDelete={() => deleteGuests(selectedItems)}
            />
          : <div className="ev-toolbar">
              <Input
                className="ev-search"
                type="text"
                name="guest_search"
                value={search}
                placeholder="Sök namn, företag, e‑post…"
                icon="filter"
                isNullable
                onChange={(e) => setSearch(e.target ? e.target.value : (e.value || ''))}
              />
              <FilterPopover
                fieldDefs={fieldDefs}
                guests={guestsFlat}
                filters={filters}
                setFilters={setFilters}
                activeCount={activeFilterCount}
              />
              <span className="ev-toolbar-spacer" />
              <Button value="Skicka inbjudan" className="button" onClick={openSendInvitesDialog} />
              <Button value="+ Lägg till gäster" className="button main-button" onClick={addGuests} />
            </div>
        }

        {chips.length > 0 &&
          <div className="ev-chips">
            {chips.map((c, i) => (
              <button type="button" key={i} className="ev-chip" onClick={c.remove}>
                {c.label} <span className="ev-chip-x">✕</span>
              </button>
            ))}
            <button type="button" className="ev-chip-clear" onClick={() => setFilters(EMPTY_FILTERS)}>Rensa alla</button>
          </div>
        }
      </div>

      <div className="ev-guestlist-table">
        <DataTable
          columns={[
            { id: 'event_guest_name', name: 'Namn' },
            { id: 'event_guest_company_name', name: 'Företag', className: 'prio-2' },
            { id: 'event_guest_email', name: 'E-post', className: 'prio-2' },
            { id: 'invite_sent', name: 'Inbjudan', type: 'boolean', values: BOOL_PILLS },
            { id: 'event_guest_response', value: 'event_guest_response', name: 'Svar', type: 'pill', values: RESPONSE_PILLS },
            ...dynamicColumns,
          ]}
          buttons={[
            { label: 'Redigera', action: 'edit', hideInToolbar: true, onClick: (items) => editGuest(items || selectedItems) },
            { label: 'Ta bort', action: 'delete', hideInToolbar: true, onClick: (items) => deleteGuests(items || selectedItems) },
          ]}
          data={filteredGuests}
          idColumn='event_guest_uuid'
          primaryColumn='event_guest_name'
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          loading={loading}
          editable={true}
          hideToolbar={true}
          filterable={false}
        />
      </div>
    </div>
  )
}

export default GuestList
